import { useMemo, useState, useEffect } from "react";
import axios from "axios";
import {
  Bot, FileJson, ListChecks, Loader2, Search, ShieldAlert, ShieldCheck,
  ChevronDown, ChevronRight, CheckCircle2, XCircle, Eye, Filter, Activity
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { API } from "@/modules/collector_v2/lib/api";
import { signalLabels } from "@/modules/collector_v2/lib/constants";
import { formatDate, formatNumber, riskLevelFromScore } from "@/modules/collector_v2/lib/formatters";
import { useApi } from "@/modules/collector_v2/hooks/useApi";
import { PageHeader } from "@/modules/collector_v2/components/common/PageHeader";
import { LoadingState } from "@/modules/collector_v2/components/common/LoadingState";
import { ErrorState } from "@/modules/collector_v2/components/common/ErrorState";
import { EmptyMini } from "@/modules/collector_v2/components/common/EmptyMini";
import { RiskBadge } from "@/modules/collector_v2/components/common/RiskBadge";

const RISK_COLORS = {
  critical: { bg: "#fef2f2", border: "#fecaca", text: "#991b1b", bar: "#dc2626" },
  high:     { bg: "#fff7ed", border: "#fed7aa", text: "#9a3412", bar: "#ea580c" },
  medium:   { bg: "#fffbeb", border: "#fde68a", text: "#92400e", bar: "#d97706" },
  low:      { bg: "#f0fdf4", border: "#bbf7d0", text: "#166534", bar: "#16a34a" },
  info:     { bg: "#f9fafb", border: "#e5e7eb", text: "#374151", bar: "#6b7280" },
};

function riskLevel(score) {
  if (score >= 85) return "critical";
  if (score >= 70) return "high";
  if (score >= 50) return "medium";
  if (score >= 30) return "low";
  return "info";
}

function DetectorHealthBar({ data }) {
  if (!data) return null;
  return (
    <Card className="mb-4" data-testid="detector-health-bar">
      <CardContent className="pt-3 pb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Activity className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Detectores</span>
            <Badge variant={data.ready === data.total ? "default" : "destructive"} className="text-xs">
              {data.ready}/{data.total} activos
            </Badge>
          </div>
          <div className="h-5 w-px bg-gray-200" />
          <div className="flex gap-1.5 flex-wrap">
            {(data.detectors || []).map((d) => (
              <span
                key={d.id}
                data-testid={`health-${d.id}`}
                title={d.dep}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium"
                style={{
                  background: d.ready ? "#ecfdf5" : "#fef2f2",
                  color: d.ready ? "#059669" : "#dc2626",
                  border: `1px solid ${d.ready ? "#a7f3d0" : "#fecaca"}`,
                }}
              >
                {d.ready ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                {d.name}
              </span>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AlertCard({ alert, onAction, selectable, selected, onToggleSelect, alreadyExported, exportLabel }) {
  const [expanded, setExpanded] = useState(false);
  const level = riskLevel(alert.risk_score);
  const colors = RISK_COLORS[level];
  const subjects = (alert.subject_ids || []).join(" / ");

  return (
    <div
      data-testid="v2-alert-card"
      className="rounded-lg border overflow-hidden transition-all"
      style={{
        borderColor: selected ? "#2563eb" : (alreadyExported ? "#86efac" : colors.border),
        background: alreadyExported ? "#f0fdf4" : "#fff",
        boxShadow: selected ? "0 0 0 2px rgba(37,99,235,0.18)" : undefined,
      }}
    >
      {/* Header clickeable */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        data-testid="v2-alert-toggle"
      >
        {selectable && (
          <span
            role="checkbox"
            aria-checked={selected}
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onToggleSelect?.(alert.alert_id); }}
            onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); e.stopPropagation(); onToggleSelect?.(alert.alert_id); } }}
            data-testid="hydra-select-checkbox"
            title={alreadyExported ? "Ya exportado anteriormente — podés re-seleccionar para exportar los UIDs faltantes" : (selected ? "Quitar de la selección" : "Seleccionar para exportar a PPPoker")}
            style={{
              width: 18, height: 18, flexShrink: 0,
              borderRadius: 4,
              border: `2px solid ${selected ? "#2563eb" : (alreadyExported ? "#16a34a" : "#cbd5e1")}`,
              background: selected ? "#2563eb" : (alreadyExported ? "#dcfce7" : "#fff"),
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "all .15s",
            }}
          >
            {selected && <CheckCircle2 style={{ width: 12, height: 12, color: "#fff" }} />}
            {!selected && alreadyExported && <CheckCircle2 style={{ width: 12, height: 12, color: "#16a34a" }} />}
          </span>
        )}
        {/* Barra de riesgo lateral */}
        <div style={{ width: 4, height: 40, borderRadius: 2, background: colors.bar, flexShrink: 0 }} />

        {/* Score */}
        <div
          className="flex items-center justify-center rounded-lg font-bold text-sm tabular-nums"
          style={{ width: 42, height: 42, background: colors.bg, color: colors.text, flexShrink: 0 }}
        >
          {alert.risk_score}
        </div>

        {/* Info principal */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-gray-900">
              {signalLabels[alert.alert_type] || (alert.alert_type === "hydra_script_launch" ? "HYDRA Script Launch" : alert.alert_type)}
            </span>
            <Badge variant="outline" className="text-[10px]">{alert.evidence_grade}</Badge>
            <Badge
              variant="outline"
              className="text-[10px]"
              style={{
                background: alert.decision === "watch_priority" ? "#fff7ed" : alert.decision === "case_review" ? "#fef2f2" : "#f9fafb",
                color: alert.decision === "watch_priority" ? "#ea580c" : alert.decision === "case_review" ? "#dc2626" : "#6b7280",
              }}
            >
              {alert.decision}
            </Badge>
            {alreadyExported && (
              <Badge
                className="text-[10px]"
                data-testid="hydra-exported-badge"
                style={{ background: "#16a34a", color: "#fff", borderColor: "#15803d" }}
                title={`Esta alerta ya fue exportada (${exportLabel || "Excel"})`}
              >
                ✅ Exportado a {exportLabel || "Excel"}
              </Badge>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate mt-0.5">{subjects}</p>
          {(alert.created_at_iso || alert.created_at) && (
            <p className="text-[10px] text-gray-400 mt-0.5 font-mono" data-testid="alert-created-at">
              📅 {new Date(alert.created_at_iso || alert.created_at).toLocaleString("es-AR", {
                day: "2-digit", month: "2-digit", year: "numeric",
                hour: "2-digit", minute: "2-digit", hour12: false,
              })}
            </p>
          )}
        </div>

        {/* Confidence + manos */}
        <div className="text-right hidden sm:block" style={{ flexShrink: 0 }}>
          <p className="text-xs text-gray-500">Conf. {alert.confidence_score}%</p>
          <p className="text-[11px] text-gray-400">{alert.sample_hand_ids?.length || 0} manos</p>
        </div>

        {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
      </button>

      {/* Detalle expandible */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t space-y-3" style={{ borderColor: colors.border }}>
          <p className="text-sm text-gray-700">{alert.explanation}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="rounded bg-gray-50 px-3 py-2"><p className="text-[10px] text-gray-400 uppercase">Detector</p><p className="text-xs font-mono font-medium">{alert.detector_version}</p></div>
            <div className="rounded bg-gray-50 px-3 py-2"><p className="text-[10px] text-gray-400 uppercase">Club</p><p className="text-xs font-medium">{(alert.club_ids || []).join(", ") || "N/D"}</p></div>
            <div className="rounded bg-gray-50 px-3 py-2"><p className="text-[10px] text-gray-400 uppercase">Union</p><p className="text-xs font-medium">{(alert.union_ids || alert.league_ids || []).join(", ") || "N/D"}</p></div>
            <div className="rounded bg-gray-50 px-3 py-2"><p className="text-[10px] text-gray-400 uppercase">Fecha</p><p className="text-xs font-medium">{(alert.created_at_iso || alert.created_at) ? formatDate(alert.created_at_iso || alert.created_at) : "N/D"}</p></div>
          </div>

          {/* Hand story */}
          {(alert.hand_story || []).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1.5">Hand Story ({alert.hand_story.length})</p>
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {alert.hand_story.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded px-3 py-1.5">
                    <span className="font-mono text-gray-600">{s.hand_id}</span>
                    <span className="text-gray-500">{s.reason || (s.aggressor_action ? `${s.aggressor_action} -> ${s.response_action}` : `Flujo ${formatNumber(s.flujo_visual_en_mano)}`)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Acciones rapidas */}
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onAction(alert.alert_id, "confirmed")} data-testid="alert-action-confirm">
              <CheckCircle2 className="h-3 w-3 mr-1 text-green-600" /> Confirmar
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onAction(alert.alert_id, "false_positive")} data-testid="alert-action-fp">
              <XCircle className="h-3 w-3 mr-1 text-red-500" /> Falso Positivo
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onAction(alert.alert_id, "case_review")} data-testid="alert-action-investigate">
              <Eye className="h-3 w-3 mr-1 text-amber-500" /> Investigar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function V2AlertsPage() {
  const [running, setRunning] = useState(false);
  const [runProgress, setRunProgress] = useState("");
  const [q, setQ] = useState("");
  const [decision, setDecision] = useState("all");
  const [alertType, setAlertType] = useState("all");
  const [clubId, setClubId] = useState("");
  const [unionId, setUnionId] = useState("");
  const [healthData, setHealthData] = useState(null);
  const [selectedHydraIds, setSelectedHydraIds] = useState(() => new Set());
  const [exportStats, setExportStats] = useState({ total_exported_uids: 0, total_exported_alerts: 0, last_exported_at: null });
  const [exportedAlertIds, setExportedAlertIds] = useState(() => new Set());
  const [genericExportedAlertIds, setGenericExportedAlertIds] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/v2/detectors/health`).then((r) => setHealthData(r.data)).catch(() => {});
  }, []);

  // Carga inicial y refresh de stats + lista de alertas ya exportadas
  const refreshExportStats = async () => {
    try {
      const r = await axios.get(`${API}/v2/brain/alerts/hydra/exported-uids/stats`);
      if (r.data?.ok) setExportStats({
        total_exported_uids: r.data.total_exported_uids || 0,
        total_exported_alerts: r.data.total_exported_alerts || 0,
        last_exported_at: r.data.last_exported_at || null,
      });
    } catch (e) { /* silencioso */ }
    try {
      const r2 = await axios.get(`${API}/v2/brain/alerts/hydra/exported-alerts`);
      if (r2.data?.ok) {
        const ids = (r2.data.items || []).map((x) => x.alert_id);
        setExportedAlertIds(new Set(ids));
      }
    } catch (e) { /* silencioso */ }
    // 🆕 Stats genéricas (no-HYDRA: bot_metronome, chipdumping, team_cluster, etc.)
    try {
      const r3 = await axios.get(`${API}/v2/brain/alerts/generic-export/exported-alerts`);
      if (r3.data?.ok) {
        const ids = (r3.data.items || []).map((x) => x.alert_id);
        setGenericExportedAlertIds(new Set(ids));
      }
    } catch (e) { /* silencioso */ }
  };
  useEffect(() => { refreshExportStats(); }, []);

  const toggleHydraSelection = (alertId) => {
    setSelectedHydraIds((prev) => {
      const next = new Set(prev);
      if (next.has(alertId)) next.delete(alertId); else next.add(alertId);
      return next;
    });
  };
  const clearHydraSelection = () => setSelectedHydraIds(new Set());

  const handleExportHydraToPPPoker = async () => {
    if (selectedHydraIds.size === 0 || exporting) return;
    setExporting(true);
    try {
      const r = await axios.post(
        `${API}/v2/brain/alerts/hydra/export-uids`,
        { alert_ids: Array.from(selectedHydraIds) },
        { responseType: "blob" },
      );
      // Si la respuesta es JSON ("no_new_uids"), no XLSX
      const ctype = r.headers["content-type"] || "";
      if (ctype.includes("application/json")) {
        const text = await r.data.text();
        const payload = JSON.parse(text);
        toast.warning(payload.message || "No hay UIDs nuevos para exportar");
        return;
      }
      const total = r.headers["x-total-uids-in-alerts"];
      const already = r.headers["x-already-exported"];
      const exported = r.headers["x-new-exported"];
      // Disparar descarga
      let filename = "pppoker_bots_uids.xlsx";
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      if (m) filename = m[1];
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportados ${exported} UIDs nuevos (${already} ya estaban exportados de ${total} totales)`);
      clearHydraSelection();
      refreshExportStats();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error al exportar";
      toast.error(`Error al exportar: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  // 🆕 Exportador genérico — Excel con 2 hojas (UIDs + Detalle) para CUALQUIER alert_type
  const handleExportGenericToExcel = async (includeOld = false) => {
    if (selectedHydraIds.size === 0 || exporting) return;
    setExporting(true);
    try {
      const r = await axios.post(
        `${API}/v2/brain/alerts/generic-export/export`,
        { alert_ids: Array.from(selectedHydraIds), include_already_exported: includeOld },
        { responseType: "blob" },
      );
      const ctype = r.headers["content-type"] || "";
      if (ctype.includes("application/json")) {
        const text = await r.data.text();
        const payload = JSON.parse(text);
        toast.warning(payload.message || "No hay UIDs nuevos para exportar", {
          description: "Activá 'Reexportar' para volver a bajar UIDs ya exportados.",
        });
        return;
      }
      const total = r.headers["x-total-uids-in-alerts"];
      const already = r.headers["x-already-exported"];
      const exported = r.headers["x-new-exported"];
      let filename = "alertas_uids.xlsx";
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      if (m) filename = m[1];
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`📥 Excel descargado — ${exported} UIDs nuevos (${already} ya exportados, ${total} totales)`);
      clearHydraSelection();
      refreshExportStats();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error al exportar";
      toast.error(`Error al exportar a Excel: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  const alertsQuery = useMemo(() => {
    const search = new URLSearchParams({ limit: "100" });
    if (q.trim()) search.set("q", q.trim());
    if (decision !== "all") search.set("decision", decision);
    if (alertType !== "all") search.set("alert_type", alertType);
    if (clubId.trim()) search.set("club_id", clubId.trim());
    if (unionId.trim()) search.set("union_id", unionId.trim());
    return `/v2/alerts?${search.toString()}`;
  }, [q, decision, alertType, clubId, unionId]);
  const alertsApi = useApi(alertsQuery);
  const casesApi = useApi("/v2/cases?limit=50");

  // ⭐ 2026-05-10: Cambio a flujo background + polling para evitar timeout 524
  // de Cloudflare cuando los detectores tardan >100s. El POST devuelve un
  // detector_run_id; hacemos polling al GET status hasta status="completed".
  const runDetectors = async () => {
    setRunning(true);
    setRunProgress(""); // mensaje pequeño debajo del botón
    try {
      // 1) Lanzar el run en background — debería retornar en <1s
      const launch = await axios.post(`${API}/v2/detectors/run`, { mode: "conservative" });
      const runId = launch.data?.detector_run_id;
      if (!runId) throw new Error("Backend no devolvió detector_run_id");

      toast.info("Detectores en ejecución…", {
        description: "Procesamiento en background, puede tardar 1-3 minutos.",
      });

      // 2) Polling cada 4s hasta completed/failed o timeout (10 min máx)
      const POLL_INTERVAL_MS = 4000;
      const MAX_POLLS = 150; // ~10 minutos
      let polls = 0;
      let finalDoc = null;
      while (polls < MAX_POLLS) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        polls += 1;
        try {
          const s = await axios.get(`${API}/v2/detectors/run/status/${runId}`);
          const status = s.data?.status;
          const counts = s.data?.detector_counts || {};
          const done = Object.keys(counts).length;
          setRunProgress(
            status === "running"
              ? `Ejecutando… ${done}/10 detectores procesados`
              : `Estado: ${status}`,
          );
          if (status === "completed" || status === "failed") {
            finalDoc = s.data;
            break;
          }
        } catch (pollErr) {
          // Tolerar errores transitorios de polling (Cloudflare hipo, etc.)
          // Solo mostrar warning si se acumulan.
          if (polls % 5 === 0) {
            console.warn("Polling detectors error:", pollErr?.message);
          }
        }
      }

      if (!finalDoc) {
        toast.warning("Tiempo agotado", {
          description: "El run sigue corriendo en background. Revisá la página en unos minutos.",
        });
        return;
      }

      if (finalDoc.status === "failed") {
        toast.error("Detectores fallaron", {
          description: finalDoc.error || "Error desconocido en background",
        });
        return;
      }

      // status === "completed"
      toast.success("Detectores ejecutados", {
        description: `${finalDoc.alerts_created || 0} alertas, ${finalDoc.cases_created || 0} casos`,
      });
      await alertsApi.refetch();
      await casesApi.refetch();
      axios.get(`${API}/v2/detectors/health`).then((r2) => setHealthData(r2.data)).catch(() => {});
    } catch (err) {
      toast.error("Error", { description: err.response?.data?.detail || err.message });
    } finally {
      setRunning(false);
      setRunProgress("");
    }
  };

  const handleAlertAction = async (alertId, newDecision) => {
    try {
      await axios.patch(`${API}/v2/alerts/${alertId}`, { decision: newDecision });
      toast.success(`Alerta marcada como: ${newDecision}`);
      await alertsApi.refetch();
    } catch (err) {
      toast.error("Error", { description: err.response?.data?.detail || err.message });
    }
  };

  const alerts = alertsApi.data?.items || [];
  const loading = alertsApi.loading || casesApi.loading;
  const error = alertsApi.error || casesApi.error;
  const hasFilters = q || decision !== "all" || alertType !== "all" || clubId || unionId;

  return (
    <>
      <PageHeader
        eyebrow="Detector V2 Conservador"
        title="Alertas y Casos"
        description="Sistema antifraude con 9 detectores: chipdumping, softplay, team clusters, bot timing, GPS y mas."
        action={
          <div className="flex flex-col items-end gap-1">
            <Button data-testid="v2-run-detectors-button" onClick={runDetectors} disabled={running} size="sm">
              {running ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Bot className="h-3.5 w-3.5 mr-1" />}
              {running ? "Ejecutando…" : "Ejecutar detectores"}
            </Button>
            {running && runProgress && (
              <span
                className="text-[10px] text-muted-foreground tabular-nums"
                data-testid="v2-run-detectors-progress"
              >
                {runProgress}
              </span>
            )}
          </div>
        }
      />

      {/* Barra de salud de detectores */}
      <DetectorHealthBar data={healthData} />

      {/* Filtros */}
      <Card className="mb-4" data-testid="v2-alerts-filter-card">
        <CardContent className="pt-3 pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 flex-1 min-w-[140px] max-w-[240px] border rounded-md px-2.5 py-1.5 bg-gray-50">
              <Search className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              <input data-testid="v2-alerts-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="bg-transparent border-0 outline-none text-sm w-full placeholder:text-gray-400" />
            </div>
            <Select value={decision} onValueChange={setDecision}>
              <SelectTrigger data-testid="v2-alerts-decision-select" className="w-[145px] h-8 text-xs"><SelectValue placeholder="Decision" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="watch">Watch</SelectItem>
                <SelectItem value="watch_priority">Watch priority</SelectItem>
                <SelectItem value="case_review">Case review</SelectItem>
                <SelectItem value="case_escalated">Escalated</SelectItem>
                <SelectItem value="confirmed">Confirmada</SelectItem>
                <SelectItem value="false_positive">Falso positivo</SelectItem>
              </SelectContent>
            </Select>
            <Select value={alertType} onValueChange={setAlertType}>
              <SelectTrigger data-testid="v2-alerts-type-select" className="w-[145px] h-8 text-xs"><SelectValue placeholder="Detector" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="chipdumping_pair">Chip Dumping</SelectItem>
                <SelectItem value="collusion_pair_softplay">Soft Play</SelectItem>
                <SelectItem value="team_cluster">Team Cluster</SelectItem>
                <SelectItem value="bot_player_timing">Bot Timing</SelectItem>
                <SelectItem value="bot_player_metronome">Bot Metronome</SelectItem>
                <SelectItem value="bomb_pot_anomaly">Bomb Pot</SelectItem>
                <SelectItem value="cross_club_account">Cross-Club</SelectItem>
                <SelectItem value="multi_account_gps">Multi-GPS</SelectItem>
                <SelectItem value="hydra_script_launch">HYDRA Script</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5 w-[90px] border rounded-md px-2 py-1.5 bg-gray-50">
              <input data-testid="v2-alerts-club-filter-input" value={clubId} onChange={(e) => setClubId(e.target.value.replace(/\D/g, ""))} placeholder="Club" className="bg-transparent border-0 outline-none text-xs w-full placeholder:text-gray-400" />
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={() => { setQ(""); setDecision("all"); setAlertType("all"); setClubId(""); setUnionId(""); }} className="text-xs text-gray-500">Limpiar</Button>
            )}
            <span className="ml-auto text-xs text-gray-400 tabular-nums">{alertsApi.data?.total || 0} alertas</span>
          </div>
        </CardContent>
      </Card>

      {loading && <LoadingState />}
      {error && <ErrorState message={error} onRetry={() => { alertsApi.refetch(); casesApi.refetch(); }} />}
      {!loading && !error && (
        <div className="space-y-4" data-testid="v2-alerts-page">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card style={{gridColumn:"auto"}}><CardContent className="flex items-center gap-3 p-4"><div style={{width:36,height:36,borderRadius:8,background:"#fef2f2",color:"#dc2626",display:"grid",placeItems:"center",flexShrink:0}}><ShieldAlert style={{width:16,height:16}}/></div><div><p className="text-xs text-gray-500">Alertas</p><p className="text-xl font-bold tabular-nums">{formatNumber(alertsApi.data?.total || 0)}</p></div></CardContent></Card>
            <Card style={{gridColumn:"auto"}}><CardContent className="flex items-center gap-3 p-4"><div style={{width:36,height:36,borderRadius:8,background:"#faf5ff",color:"#7c3aed",display:"grid",placeItems:"center",flexShrink:0}}><FileJson style={{width:16,height:16}}/></div><div><p className="text-xs text-gray-500">Casos</p><p className="text-xl font-bold tabular-nums">{formatNumber(casesApi.data?.total || 0)}</p></div></CardContent></Card>
            <Card style={{gridColumn:"auto"}}><CardContent className="flex items-center gap-3 p-4"><div style={{width:36,height:36,borderRadius:8,background:"#fff7ed",color:"#ea580c",display:"grid",placeItems:"center",flexShrink:0}}><ShieldCheck style={{width:16,height:16}}/></div><div><p className="text-xs text-gray-500">Watch Priority</p><p className="text-xl font-bold tabular-nums">{alerts.filter((a) => a.decision === "watch_priority").length}</p></div></CardContent></Card>
            <Card style={{gridColumn:"auto"}}><CardContent className="flex items-center gap-3 p-4"><div style={{width:36,height:36,borderRadius:8,background:"#ecfdf5",color:"#059669",display:"grid",placeItems:"center",flexShrink:0}}><ListChecks style={{width:16,height:16}}/></div><div><p className="text-xs text-gray-500">Hand Stories</p><p className="text-xl font-bold tabular-nums">{alerts.reduce((s, a) => s + (a.hand_story?.length || 0), 0)}</p></div></CardContent></Card>
          </div>

          {/* Lista de alertas */}
          <Card data-testid="v2-alerts-list-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Alertas ({alerts.length})</CardTitle>
              <CardDescription className="text-xs">
                Ordenadas por risk score. Click para expandir evidencia y tomar accion.
                {" "}
                <span className="text-blue-600">Marcá el checkbox de cada alerta para copiar sus UIDs en una sola columna o exportar a PPPoker (HYDRA)</span>
                {" · "}
                <span className="text-gray-500">UIDs ya exportados: <strong className="tabular-nums">{exportStats.total_exported_uids}</strong></span>
                {" · "}
                <span className="text-green-600">Alertas exportadas: <strong className="tabular-nums">{exportStats.total_exported_alerts}</strong></span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length === 0
                ? <EmptyMini label="Sin alertas con los filtros actuales" />
                : <div className="space-y-2">{alerts.map((a) => {
                    const isHydra = a.alert_type === "hydra_script_launch";
                    const wasExportedHydra = isHydra && exportedAlertIds.has(a.alert_id);
                    const wasExportedExcel = genericExportedAlertIds.has(a.alert_id);
                    const wasExported = wasExportedHydra || wasExportedExcel;
                    // Si es HYDRA y se exportó tanto a PPPoker como a Excel, prioriza PPPoker (más fuerte)
                    const exportLabel = wasExportedHydra ? "PPPoker" : "Excel";
                    return (
                      <AlertCard
                        key={a.alert_id}
                        alert={a}
                        onAction={handleAlertAction}
                        selectable={true}
                        selected={selectedHydraIds.has(a.alert_id)}
                        onToggleSelect={toggleHydraSelection}
                        alreadyExported={wasExported}
                        exportLabel={exportLabel}
                      />
                    );
                  })}</div>
              }
            </CardContent>
          </Card>
        </div>
      )}

      {/* Barra flotante de acciones sobre alertas seleccionadas */}
      {selectedHydraIds.size > 0 && (() => {
        const selectedAlerts = alerts.filter(a => selectedHydraIds.has(a.alert_id));
        const uniqueUids = Array.from(new Set(
          selectedAlerts.flatMap(a => Array.isArray(a.subject_ids) ? a.subject_ids : [])
        )).map(String);
        const allAreHydra = selectedAlerts.length > 0 && selectedAlerts.every(a => a.alert_type === "hydra_script_launch");
        const alreadyExportedCount = selectedAlerts.filter(a =>
          (a.alert_type === "hydra_script_launch" && exportedAlertIds.has(a.alert_id)) ||
          genericExportedAlertIds.has(a.alert_id)
        ).length;

        const copyUids = async () => {
          const text = uniqueUids.join("\n");
          try {
            await navigator.clipboard.writeText(text);
            toast.success(`📋 ${uniqueUids.length} UID(s) copiados al portapapeles`);
          } catch {
            const blob = new Blob([text], { type: "text/plain" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `uids_alertas_${Date.now()}.txt`; a.click();
            URL.revokeObjectURL(url);
            toast.info("UIDs descargados en archivo .txt");
          }
        };

        return (
          <div
            data-testid="alerts-action-bar"
            style={{
              position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)",
              zIndex: 50, background: "#1e293b", color: "#fff",
              padding: "10px 16px", borderRadius: 12,
              boxShadow: "0 10px 35px rgba(15,23,42,0.4)",
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              border: "1px solid #334155", maxWidth: "92vw",
            }}
          >
            <span style={{ fontSize: 13 }}>
              🎯 <strong>{selectedHydraIds.size}</strong> alerta{selectedHydraIds.size !== 1 ? "s" : ""}
              {" · "}
              <strong style={{ color: "#a7f3d0" }}>{uniqueUids.length}</strong> UID{uniqueUids.length !== 1 ? "s" : ""} únicos
              {alreadyExportedCount > 0 && (
                <span style={{ color: "#fbbf24", marginLeft: 8 }}>
                  · {alreadyExportedCount} ya exportadas
                </span>
              )}
            </span>
            <Button
              size="sm"
              onClick={() => handleExportGenericToExcel(false)}
              disabled={exporting}
              data-testid="export-excel-btn"
              style={{ background: "#16a34a", color: "#fff" }}
              title="Descarga Excel con UIDs únicos NUEVOS + hoja de Detalle (alert_type, risk, grade, decision, flags)"
            >
              {exporting
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Generando…</>
                : <>📥 Exportar a Excel</>}
            </Button>
            {alreadyExportedCount > 0 && (
              <Button
                size="sm"
                onClick={() => handleExportGenericToExcel(true)}
                disabled={exporting}
                data-testid="export-excel-reexport-btn"
                variant="outline"
                style={{ background: "#475569", color: "#fff", borderColor: "#64748b" }}
                title="Re-exporta TODOS los UIDs (incluso los ya enviados antes)"
              >
                🔄 Reexportar todo
              </Button>
            )}
            <Button
              size="sm"
              onClick={copyUids}
              data-testid="copy-uids-btn"
              variant="outline"
              style={{ background: "transparent", color: "#cbd5e1", borderColor: "#475569" }}
              title="Copia los UIDs únicos al portapapeles"
            >
              📋 Copiar
            </Button>
            {allAreHydra && (
              <Button
                size="sm"
                onClick={handleExportHydraToPPPoker}
                disabled={exporting}
                data-testid="hydra-export-btn"
                style={{ background: "#2563eb", color: "#fff" }}
                title="Exportar a PPPoker (solo HYDRA)"
              >
                📤 PPPoker
              </Button>
            )}
            <Button
              size="sm" variant="ghost"
              onClick={clearHydraSelection}
              disabled={exporting}
              data-testid="alerts-clear-selection-btn"
              style={{ color: "#cbd5e1" }}
            >
              ✕ Limpiar
            </Button>
          </div>
        );
      })()}
    </>
  );
}
